import { DEFAULT_PAGE_SIZE } from '@lib/input/pagination.input'

/**
 * How many rows a list page shows before it pages.
 *
 * Re-exported from `@lib` rather than declared here: the server slices by this
 * number and the client reads pages of it, and two copies of one number is how
 * a page of 50 quietly becomes a readout that says 25.
 */
export const PAGE_SIZE = DEFAULT_PAGE_SIZE

/** A page button, or the gap that stands for the pages nobody needs to see. */
export type PageSlot = { kind: 'page'; page: number } | { kind: 'gap'; after: number }

/**
 * Which page numbers to show, and where the gaps go.
 *
 * The rules, in the order they matter:
 *   • the first and last page are always reachable — they are where "start
 *     again" and "how far does this go" live;
 *   • the current page is always shown with a neighbor on each side, so the
 *     next click is next to the pointer;
 *   • the strip is a fixed width, so it does not resize as somebody pages
 *     through it. Seven slots: 1, a gap, three around the current page, a gap,
 *     the last. Under eight pages every page fits and no gap is drawn.
 *
 * A gap carries the page it follows so React has a stable key: two gaps in one
 * strip are not interchangeable.
 */
export function pageWindow(current: number, pageCount: number): PageSlot[] {
  if (pageCount <= 0) return []
  const page = Math.min(Math.max(1, current), pageCount)

  const all = (): PageSlot[] =>
    Array.from({ length: pageCount }, (_, i) => ({ kind: 'page' as const, page: i + 1 }))
  if (pageCount <= 7) return all()

  // Near either end the window slides rather than centering, so the strip stays
  // seven slots wide instead of shrinking as it runs out of pages to show.
  const nearStart = page <= 4
  const nearEnd = page >= pageCount - 3

  if (nearStart) {
    return [
      ...[1, 2, 3, 4, 5].map((p) => ({ kind: 'page' as const, page: p })),
      { kind: 'gap', after: 5 },
      { kind: 'page', page: pageCount },
    ]
  }
  if (nearEnd) {
    return [
      { kind: 'page', page: 1 },
      { kind: 'gap', after: 1 },
      ...[pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount].map((p) => ({
        kind: 'page' as const,
        page: p,
      })),
    ]
  }
  return [
    { kind: 'page', page: 1 },
    { kind: 'gap', after: 1 },
    { kind: 'page', page: page - 1 },
    { kind: 'page', page },
    { kind: 'page', page: page + 1 },
    { kind: 'gap', after: page + 1 },
    { kind: 'page', page: pageCount },
  ]
}

/** How many pages a list of this length needs. Zero rows is still one page. */
export function pageCountFor(total: number, perPage: number = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / perPage))
}

/**
 * The slice of a list one page shows, and the numbers the readout says.
 *
 * `page` is clamped rather than trusted. A filter that shortens the list leaves
 * the current page past the end — the row count drops from 300 to 12 while the
 * page says 6 — and an unclamped slice is an empty table with no explanation.
 */
export function pageOf<T>(
  items: readonly T[],
  page: number,
  perPage: number = PAGE_SIZE,
): { items: T[]; page: number; pageCount: number; from: number; to: number; total: number } {
  const pageCount = pageCountFor(items.length, perPage)
  const clamped = Math.min(Math.max(1, page), pageCount)
  const start = (clamped - 1) * perPage
  const slice = items.slice(start, start + perPage)
  return {
    items: slice,
    page: clamped,
    pageCount,
    // One-based and inclusive, because the readout is for people: "51–75 of
    // 340", not "50–74".
    from: items.length === 0 ? 0 : start + 1,
    to: start + slice.length,
    total: items.length,
  }
}

/**
 * The same numbers as `pageOf`, for a list the server has already sliced.
 *
 * The API answers with the page's rows and the count of the whole list, which is
 * everything the bar needs — but the arithmetic of turning that into "51–75 of
 * 340" belongs in one place, next to the version that does its own slicing.
 */
export function serverPage(
  page: number,
  total: number,
  rowCount: number,
  perPage: number = PAGE_SIZE,
): { page: number; pageCount: number; from: number; to: number; total: number } {
  const pageCount = pageCountFor(total, perPage)
  const clamped = Math.min(Math.max(1, page), pageCount)
  const start = (clamped - 1) * perPage
  return {
    page: clamped,
    pageCount,
    from: rowCount === 0 ? 0 : start + 1,
    to: start + rowCount,
    total,
  }
}

/**
 * `?page=&limit=` for a paged request, or an empty string.
 *
 * Empty when no page is asked for, because the API treats absent paging as "the
 * whole list" — which is what keeps those endpoints unbroken for the API tokens
 * already calling them.
 */
export function pageQuery(page?: number): string {
  return listQuery({}, page)
}

/**
 * `?a=b&page=&limit=` for a filtered, paged list.
 *
 * Values that are `undefined`, `null` or empty are left out entirely rather
 * than sent empty: the list queries are `strict`, and `?q=` would be a filter
 * asking for events whose name contains nothing.
 */
export function listQuery(
  filters: Record<string, string | number | boolean | null | undefined>,
  page?: number,
): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  if (page !== undefined) {
    params.set('page', String(page))
    params.set('limit', String(PAGE_SIZE))
  }
  const query = params.toString()
  return query === '' ? '' : `?${query}`
}
